/**
 * Directory search, filters and sorting (docs/directory-plan.md section 9).
 * Pure logic: no Firebase, no i18n, no clock. The useDirectorySearch hook feeds
 * it labels, open statuses and the URL.
 */
import type { CategoryKey, LanguageCode, Listing, OpenStatus, ShiurAudience } from '../types';
import { LatLng, distanceMeters, mapPointOf } from './geo';

// ---------- Text normalizing ----------

const HEBREW_FINAL_LETTERS: Record<string, string> = {
  ך: 'כ',
  ם: 'מ',
  ן: 'נ',
  ף: 'פ',
  ץ: 'צ',
};

/**
 * Makes text comparable: lower case, no nikud/cantillation or accents, no
 * quote marks (״ ׳ " ' ` ’), final Hebrew letters as regular letters, single spaces.
 * So "גמ״ח" and "גמח" match, and "שָׁלוֹם" matches "שלום".
 */
export function normalizeSearchText(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[̀-֑ͯ-ׇ]/g, '') // accents, nikud, cantillation, maqaf
    .replace(/[״׳"'`’‘]/g, '')
    .replace(/[ךםןףץ]/g, (c) => HEBREW_FINAL_LETTERS[c])
    .toLowerCase()
    .replace(/[\s\-–—/.,:;()]+/g, ' ')
    .trim();
}

/** Labels for a subcategory or category key, in every language (from i18n). */
export type LabelLookup = (kind: 'category' | 'sub', key: string) => string[];

/** All the searchable text of a listing, normalized, in one string. */
export function listingSearchText(listing: Listing, labels: LabelLookup): string {
  const sub = 'subcategory' in listing ? listing.subcategory : undefined;
  return normalizeSearchText(
    [
      listing.nameEn,
      listing.nameHe,
      ...labels('category', listing.category),
      ...(sub ? labels('sub', sub) : []),
      ...(listing.tags ?? []),
      // Shiurim: who gives it and the owner's own topic.
      ...(listing.category === 'shiur'
        ? [listing.details?.giverHe, listing.details?.giverEn, listing.details?.topicOtherHe, listing.details?.topicOtherEn]
        : []),
      listing.descriptionEn,
      listing.descriptionHe,
    ]
      .filter(Boolean)
      .join(' ')
  );
}

/** Every word of the query must appear somewhere in the listing (in any order). */
export function matchesQuery(searchText: string, query: string): boolean {
  const words = normalizeSearchText(query).split(' ').filter(Boolean);
  return words.every((w) => searchText.includes(w));
}

// ---------- Filters ----------

export type FreeFilter = 'free' | 'rent';

export interface DirectoryFilters {
  /** Search text as typed. */
  q: string;
  /** Category chips (All Services page only). Empty = all. */
  cat: CategoryKey[];
  /** Subcategory chips. Empty = all. */
  sub: string[];
  /** Only listings open right now. */
  open: boolean;
  /** Batei Horaah: answers in this language. Shiurim: given in this language. */
  lang?: LanguageCode;
  /** Shiurim: for this group ("everyone" shiurim always count). */
  aud?: ShiurAudience;
  /** Shiurim: there is one still to come today. */
  today: boolean;
  /** Mikvaos: wheelchair accessible only. */
  accessible: boolean;
  /** Shabbos apartments: free (hachnosas orchim) or for rent. */
  free?: FreeFilter;
}

export const EMPTY_FILTERS: DirectoryFilters = {
  q: '',
  cat: [],
  sub: [],
  open: false,
  accessible: false,
  today: false,
};

/** Category-specific filters, shown on that category's page. */
export type ExtraFilterKey = 'lang' | 'accessible' | 'free' | 'aud' | 'today';

export const CATEGORY_EXTRA_FILTERS: Record<CategoryKey, ExtraFilterKey[]> = {
  shul: [],
  gemach: [],
  mikvah: ['accessible'],
  beisHoraah: ['lang'],
  organization: [],
  service: ['free'],
  shiur: ['today', 'aud', 'lang'],
};

const LANGUAGE_CODES: LanguageCode[] = ['he', 'en', 'yi', 'fr', 'es', 'ru'];
const AUDIENCES: ShiurAudience[] = ['men', 'women', 'children', 'everyone'];
const CATEGORY_KEYS: CategoryKey[] = ['shul', 'gemach', 'mikvah', 'beisHoraah', 'organization', 'service', 'shiur'];

function listParam(value: string | null): string[] {
  return (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** URL → filters (`?q=&cat=&sub=&open=1&lang=&accessible=1&free=&aud=&today=1`). Unknown values are ignored. */
export function parseDirectoryParams(params: URLSearchParams): DirectoryFilters {
  const lang = params.get('lang');
  const free = params.get('free');
  const aud = params.get('aud');
  return {
    q: params.get('q') ?? '',
    cat: listParam(params.get('cat')).filter((c): c is CategoryKey =>
      CATEGORY_KEYS.includes(c as CategoryKey)
    ),
    sub: listParam(params.get('sub')),
    open: params.get('open') === '1',
    lang: LANGUAGE_CODES.includes(lang as LanguageCode) ? (lang as LanguageCode) : undefined,
    accessible: params.get('accessible') === '1',
    free: free === 'free' || free === 'rent' ? free : undefined,
    aud: AUDIENCES.includes(aud as ShiurAudience) ? (aud as ShiurAudience) : undefined,
    today: params.get('today') === '1',
  };
}

/** Filters → URL params; empty filters are left out so URLs stay short. */
export function directoryParamsFromFilters(filters: DirectoryFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q.trim()) params.set('q', filters.q);
  if (filters.cat.length) params.set('cat', filters.cat.join(','));
  if (filters.sub.length) params.set('sub', filters.sub.join(','));
  if (filters.open) params.set('open', '1');
  if (filters.lang) params.set('lang', filters.lang);
  if (filters.accessible) params.set('accessible', '1');
  if (filters.free) params.set('free', filters.free);
  if (filters.aud) params.set('aud', filters.aud);
  if (filters.today) params.set('today', '1');
  return params;
}

/** True when anything besides the default view is selected. */
export function hasActiveFilters(filters: DirectoryFilters): boolean {
  return directoryParamsFromFilters(filters).toString() !== '';
}

/** "Open now" counts listings that are open or closing soon. */
export function isOpenNow(status: OpenStatus | undefined): boolean {
  return status === 'open' || status === 'closesSoon';
}

export function passesFilters(
  listing: Listing,
  filters: DirectoryFilters,
  status: OpenStatus | undefined,
  /** Shiurim: there is one still to come today. */
  shiurToday = false
): boolean {
  if (filters.cat.length && !filters.cat.includes(listing.category)) return false;
  if (filters.sub.length) {
    const sub = 'subcategory' in listing ? listing.subcategory : undefined;
    if (!sub || !filters.sub.includes(sub)) return false;
  }
  // Emergency organizations stay visible even when "closed" (plan 6.4).
  if (filters.open && !isOpenNow(status) && !isEmergency(listing)) return false;

  if (filters.lang) {
    if (listing.category !== 'beisHoraah' && listing.category !== 'shiur') return false;
    if (!(listing.details?.languages ?? []).includes(filters.lang)) return false;
  }
  if (filters.aud) {
    if (listing.category !== 'shiur') return false;
    const audience = listing.details?.audience ?? [];
    if (!audience.includes(filters.aud) && !audience.includes('everyone')) return false;
  }
  if (filters.today && !shiurToday) return false;
  if (filters.accessible) {
    if (listing.category !== 'mikvah' || listing.details.accessible !== true) return false;
  }
  if (filters.free) {
    if (listing.category !== 'service' || typeof listing.details.free !== 'boolean') return false;
    if (listing.details.free !== (filters.free === 'free')) return false;
  }
  return true;
}

// ---------- Sorting ----------

export function isEmergency(listing: Listing): boolean {
  return listing.category === 'organization' && listing.details.emergency === true;
}

/** Name in the current language, falling back to the other one. */
export function displayName(listing: Pick<Listing, 'nameEn' | 'nameHe'>, isHebrew: boolean): string {
  return (isHebrew ? listing.nameHe || listing.nameEn : listing.nameEn || listing.nameHe) ?? '';
}

/** 0 = open now, 1 = opens soon, 2 = everything else (closed, call to check, shuls). */
function openRank(status: OpenStatus | undefined): number {
  if (isOpenNow(status)) return 0;
  if (status === 'opensSoon') return 1;
  return 2;
}

/**
 * Sort: emergency organizations always first; then "open now first" (unless
 * the Open now filter is on, when everything shown is open); then by name in
 * the current language.
 */
export function compareListings(
  a: Listing,
  b: Listing,
  statusOf: (l: Listing) => OpenStatus | undefined,
  isHebrew: boolean,
  openFilterOn: boolean
): number {
  const emergency = Number(isEmergency(b)) - Number(isEmergency(a));
  if (emergency) return emergency;
  if (!openFilterOn) {
    const open = openRank(statusOf(a)) - openRank(statusOf(b));
    if (open) return open;
  }
  return displayName(a, isHebrew).localeCompare(displayName(b, isHebrew), isHebrew ? 'he' : 'en');
}

// ---------- All together ----------

/**
 * Distance from `origin` to where the listing shows on the map (the soft
 * circle's centre for hidden locations). Undefined without coordinates.
 */
export function listingDistance(listing: Listing, origin: LatLng): number | undefined {
  const point = mapPointOf(listing.location);
  return point ? distanceMeters(origin, point) : undefined;
}

/**
 * "Nearest" sort, used after the user taps Near me: emergency organizations
 * still first, then by distance (listings without a location last), then name.
 */
export function compareByDistance(
  a: Listing,
  b: Listing,
  distanceOf: (l: Listing) => number | undefined,
  isHebrew: boolean
): number {
  const emergency = Number(isEmergency(b)) - Number(isEmergency(a));
  if (emergency) return emergency;
  const da = distanceOf(a) ?? Infinity;
  const db = distanceOf(b) ?? Infinity;
  if (da !== db) return da - db;
  return displayName(a, isHebrew).localeCompare(displayName(b, isHebrew), isHebrew ? 'he' : 'en');
}

export interface SearchInput {
  listings: Listing[];
  filters: DirectoryFilters;
  labels: LabelLookup;
  statusOf: (listing: Listing) => OpenStatus | undefined;
  isHebrew: boolean;
  /** The user's location after tapping Near me: sorts nearest first. */
  origin?: LatLng | null;
  /** Shiurim with one still to come today (the Today filter). */
  shiurToday?: (listing: Listing) => boolean;
}

export function searchDirectory({ listings, filters, labels, statusOf, isHebrew, origin, shiurToday }: SearchInput): Listing[] {
  const query = filters.q.trim();
  const matched = listings
    .filter((l) => passesFilters(l, filters, statusOf(l), shiurToday?.(l) ?? false))
    .filter((l) => !query || matchesQuery(listingSearchText(l, labels), query));
  if (origin) {
    const distances = new Map(matched.map((l) => [l.id, listingDistance(l, origin)]));
    return matched.sort((a, b) => compareByDistance(a, b, (l) => distances.get(l.id), isHebrew));
  }
  return matched.sort((a, b) => compareListings(a, b, statusOf, isHebrew, filters.open));
}
